export interface SearchEngine {
  id: string;
  label: string;
  url: string;
  icon?: string;
  privacyUrl?: string;
  enabled: boolean;
  isCustom: boolean;
}

const STORAGE_KEY = "kourai-search-engines";

const BUILT_IN: SearchEngine[] = [
  {
    id: "google",
    label: "Google",
    url: "https://www.google.com/search?q={query}",
    icon: "https://www.google.com/favicon.ico",
    enabled: true,
    isCustom: false,
  },
  {
    id: "bing",
    label: "Bing",
    url: "https://www.bing.com/search?q={query}",
    icon: "https://www.bing.com/favicon.ico",
    enabled: true,
    isCustom: false,
  },
  {
    id: "duckduckgo",
    label: "DuckDuckGo",
    url: "https://duckduckgo.com/?q={query}",
    icon: "https://duckduckgo.com/favicon.ico",
    privacyUrl: "https://duckduckgo.com/about/privacy",
    enabled: true,
    isCustom: false,
  },
  {
    id: "yahoo",
    label: "Yahoo",
    url: "https://search.yahoo.com/search?p={query}",
    icon: "https://search.yahoo.com/favicon.ico",
    enabled: false,
    isCustom: false,
  },
  {
    id: "brave",
    label: "Brave",
    url: "https://search.brave.com/search?q={query}",
    icon: "https://brave.com/favicon.ico",
    privacyUrl: "https://brave.com/privacy/",
    enabled: false,
    isCustom: false,
  },
];

function migrateLegacyEngine() {
  try {
    const legacy = localStorage.getItem("kourai-search-engine");
    if (!legacy || localStorage.getItem(STORAGE_KEY)) return;
    localStorage.removeItem("kourai-search-engine");
  } catch {
    /* ignore */
  }
}

export function getSearchEngines(): SearchEngine[] {
  migrateLegacyEngine();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as SearchEngine[];
      const storedIds = new Set(stored.map((e) => e.id));
      const merged = [...stored];
      for (const builtIn of BUILT_IN) {
        if (!storedIds.has(builtIn.id)) {
          merged.push(builtIn);
        }
      }
      return merged;
    }
  } catch {
    /* ignore */
  }
  return BUILT_IN;
}

function saveSearchEngines(engines: SearchEngine[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(engines));
  } catch {
    /* ignore */
  }
}

export function updateSearchEngine(id: string, updates: Partial<SearchEngine>) {
  const engines = getSearchEngines();
  const idx = engines.findIndex((e) => e.id === id);
  if (idx === -1) return;
  engines[idx] = { ...engines[idx], ...updates } as SearchEngine;
  saveSearchEngines(engines);
}

export function addSearchEngine(engine: Omit<SearchEngine, "id" | "isCustom">): SearchEngine {
  const engines = getSearchEngines();
  const id = `custom-${crypto.randomUUID()}`;
  const newEngine: SearchEngine = { ...engine, id, isCustom: true };
  engines.push(newEngine);
  saveSearchEngines(engines);
  return newEngine;
}

export function removeSearchEngine(id: string) {
  const engines = getSearchEngines();
  saveSearchEngines(engines.filter((e) => e.id !== id));
}

export function getEnabledEngines(): SearchEngine[] {
  return getSearchEngines().filter((e) => e.enabled);
}
